import React, { Component } from 'react';
import { useLocalization, useTranslate } from '@im/react-localization';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { styled } from '@mui/material/styles';

type Props = {
  children: React.ReactNode;
  fallback?: JSX.Element;
  scope: string;
};

type State = {
  fallback?: JSX.Element;
  hasError: boolean;
  scope: string;
};

export class ErrorBoundary extends Component<Props, State> {
  state: State = {
    fallback: this.props.fallback,
    hasError: false,
    scope: this.props.scope,
  };

  static getDerivedStateFromProps(props: Props, state: State) {
    // When the scope changes, reset hasError.
    if (props.scope !== state.scope) {
      return {
        hasError: false,
        scope: props.scope,
      };
    }
    return null;
  }

  static getDerivedStateFromError(error: any) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error: any, errorInfo: any) {
    // You can also log the error to an error reporting service
    // logErrorToMyService(error, errorInfo);
    console.error(`[ErrorBoundary - ${this.props.scope}]`, error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <>{this.state.fallback ? this.state.fallback : <ErrorContent />}</>
      );
    }

    return <>{this.props.children}</>;
  }
}

function ErrorContent() {
  const { translate } = useLocalization();
  const translateStr = useTranslate();

  return (
    <Root>
      <img
        alt={translateStr('Common.Somethingwentwrong')}
        src="/cep/assets/img/errorBoundary.svg"
        style={{ marginBottom: '50px' }}
      />
      <Typography sx={{ fontSize: '14px', lineHeight: '22px' }}>
        {translate('ErrorBoundary.ErrorText', undefined, true)}
      </Typography>

      <Typography
        sx={{ fontSize: '20px', lineHeight: '28px', margin: '32px 0px' }}
        variant="h2"
      >
        {translate('Common.PageErrorMainText')}
      </Typography>
      <Button
        href="/cep/app/home"
        startIcon={<ArrowBackIcon />}
        sx={{ fontSize: '14px', lineHeight: '22px', textTransform: 'none' }}
        variant="contained"
      >
        {translate('Common.BackToHome')}
      </Button>
    </Root>
  );
}
const Root = styled('div')(() => ({
  margin: '50px auto 0px',
  maxWidth: '1500px',
  textAlign: 'center',
}));
